import React, { useState } from "react";
import {
  formatDate,
  formatDateToIST,
  getCachedMediaUrl,
  getInitials,
} from "../../../../../lib/utils/utils";
import ChevronDown from "../../../../../Assets/ChevronDownLargeDarkMode.svg";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../../Components/Toaster/Toaster";
import {
  inviteSellerMember,
  removeSellerMember,
} from "../../../../../lib/utils/apiCalls";
import { fetchSellerMembers } from "../../../../../Features/settingsSlice";

const rolesList = [
  {
    id: "admin",
    role: "Admin",
  },
  {
    id: "store-manager",
    role: "Store Manager",
  },
  {
    id: "inventory-manager",
    role: "Inventory Manager",
  },
  {
    id: "lens-manager",
    role: "Lens Manager",
  },
];

function Member({ member, openDropdownMemberId, setOpenDropdownMemberId }) {
  const user = useSelector((state) => state.user.user); 
  const [memberRoles, setMemberRoles] = useState({});

  const toggleDropdown = (memberId) => {
    setOpenDropdownMemberId((prevId) =>
      prevId === memberId ? null : memberId
    );
  };

  const handleRoleToggle = (memberId, role) => {
    setMemberRoles((prev) => {
      const currentRoles = prev[memberId] || [];
      const roleExists = currentRoles.includes(role);

      const updatedRoles = roleExists
        ? currentRoles.filter((r) => r !== role)
        : [...currentRoles, role];

      return {
        ...prev,
        [memberId]: updatedRoles,
      };
    });
  };

  const dispatch = useDispatch();

  async function handleRemoveSeller() {
    try {
      const response = await removeSellerMember(member.id);
      if (response.status === 200) {
        showToast("Removed seller", "success");
        dispatch(fetchSellerMembers());
      }
    } catch (err) {
      showToast(err, "error");
    }
  }

  async function handleResendInvite() {
    try {
      const callBody = {
        email: member.email,
        role: member.role.map((role) => role),
      };

      const response = await inviteSellerMember(callBody);
      if (response.status === 200) {
        showToast("Seller invite resent", "success");
      }
    } catch (err) {
      showToast(err, "error");
    }
  }

  return (
    <div className="w-full flex items-center border-t border-[#EAEAEA]">
      <div className="w-[28%] pl-[10px] py-4 flex items-center gap-[10px]">
        <div className="size-[46px] rounded-full overflow-hidden bg-[#EAEAEA] flex items-center justify-center">
          {member.profilePic ? (
            <img
              className="size-full object-cover"
              src={getCachedMediaUrl(member.profilePic)}
              alt=""
            />
          ) : (
            <p className="font-bold text-base text-[#666666] font-mulish">
              {getInitials(member.name)}
            </p>
          )}
        </div>

        <div className="w-[calc(100%-46px)] flex flex-col gap-[2px]">
          <h1 className="font-semibold text-[14px] leading-6 text-[#121212] line-clamp-1">
            {member.name} {user.id === member.id && `(You)`}
          </h1>

          <p className="font-medium text-[12px] leading-[22px] text-[#808080]">
            {member.email}
          </p>
        </div>
      </div>
      <div className="w-[20%] relative">
        {member.id === user.id ? (
          <p className="px-4 py-2 font-medium text-[14px] leading-[17px] text-[#121212]">
            {member.role.map((role) => role).join(", ")}
          </p>
        ) : (
          <div
            className="px-4 py-2 flex items-center justify-between relative cursor-pointer"
            onClick={() => toggleDropdown(member.id)}
          >
            <p className="font-medium text-[14px] leading-[17px] text-[#121212]">
              {
                memberRoles[member.id]?.[0] || member.role[0]
              }
              {(memberRoles[member.id]?.length || member.role.length) > 1 &&
                ` +${
                  (memberRoles[member.id]?.length || member.role.length) - 1
                } More`}
            </p>

            <div className="size-3">
              <img className="size-full" src={ChevronDown} alt="" />
            </div>

            {openDropdownMemberId === member.id && (
              <div className="absolute top-full mt-1 left-0 bg-white border rounded shadow z-10 w-48">
                {rolesList.map((roleObj) => {
                  const selectedRoles = memberRoles[member.id] || member.role;
                  const isSelected = selectedRoles.includes(roleObj.role);

                  return (
                    <div
                      key={roleObj.id}
                      className="px-4 py-2 hover:bg-gray-100 text-sm cursor-pointer flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRoleToggle(member.id, roleObj.role);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="mr-2"
                      />
                      {roleObj.role}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="w-[20%] px-4 py-2">
        <p className="font-medium text-[14px] leading-[17px] text-[#121212]">
          {member.joinedAt ? formatDate(member.joinedAt) : "-"}
        </p>
      </div>

      <div className="w-[20%] px-4 py-2">
        <p className="font-medium text-[14px] leading-[17px] text-[#121212]">
          {formatDateToIST(member.lastLogin)}
        </p>
      </div>

      <div className="w-[12%] flex items-center justify-center font-medium text-[14px] leading-[17px]">
        {user.id === member.id ? (
          <button className="text-[#EF4444] outline-none">Leave</button>
        ) : (
          <div className="flex items-center gap-[10px]">
            {member.joinedAt ? (
              <button
                onClick={handleRemoveSeller}
                className="outline-none text-[#EF4444]"
              >
                Remove
              </button>
            ) : (
              <>
                <button
                  onClick={handleResendInvite}
                  className="outline-none text-primaryColor"
                >
                  Resend
                </button>

                <button onClick={handleRemoveSeller} className="text-[#EF4444]">
                  Revoke
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Member;
